import axios, { AxiosError } from 'axios';
import config from 'config';
import i18n from 'i18next';

import { jwt } from './jwt';
import { refreshTokenStorage, tokenStorage } from './storage';

// REST base for the migrated /api/v2 doctor BE (bare response bodies,
// accessToken/refreshToken fields, 1-based pagination).
// i18n translations (aws-s3/get-data) stay on config.base_url — that route
// only exists on the legacy gateway (see doctor-front-p0-infra.FE-FLAG-BACK.log).
export const doctorApiV2BaseUrl = `${config.base_url}/api/v2`;

type ServerError = {
  [key: string]: {
    [key: string]: {
      message: string;
    };
  };
};

type FieldsData = {
  name: string;
  errors: string[];
};

export const getFieldsDataFromServerError: (
  errors: ServerError
) => FieldsData[] = (errors: ServerError) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const error = Object.keys(errors).reduce((acc: any[], key: string) => {
    return [
      ...acc,
      { name: key, errors: [errors[key].message || errors[key].msg] }
    ];
  }, []);

  return error as FieldsData[];
};

export const instance = axios.create({
  baseURL: doctorApiV2BaseUrl
});

instance.interceptors.request.use(
  request => {
    const token = tokenStorage.get();

    if (token && request.headers) {
      request.headers['Authorization'] = `Bearer ${token}`;
    }

    // default UI language; callers may override per request (e.g. the
    // profile DATA locale from the ?lang= URL param — hard lesson E)
    if (request.headers && !request.headers['locale']) {
      request.headers['locale'] = i18n.language || 'en';
    }

    return request;
  },
  error => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // Only try a token refresh when there's a session to refresh. A 401 on
    // sign-in (or any request made while logged out) is a real error — bad
    // credentials — not an expired token, so we must NOT swallow it; let it
    // fall through to the message rejection below so callers see the reason.
    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      refreshTokenStorage.get()
    ) {
      originalRequest._retry = true;

      const newToken = await jwt.refreshToken();

      if (newToken) {
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

        return instance(originalRequest);
      }
      // refresh failed (tokens already cleared in jwt.refreshToken) — fall
      // through and reject with the original error's message.
    }

    if (error.response?.status === 422) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorData: any = error?.response?.data;

      return new Promise((_, reject) => {
        if (errorData) {
          if (errorData.errors) {
            const errors = getFieldsDataFromServerError(errorData.errors);

            return reject({ ...errorData, errors });
          }

          return reject({ ...errorData, status: error?.response.status });
        }

        return reject(error);
      });
    }

    let errorMessage = 'Something went wrong';

    if (error instanceof AxiosError) {
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error.response?.data === 'string') {
        errorMessage = error.response.data;
      } else if (error.message) {
        errorMessage = error.message;
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    return Promise.reject(errorMessage);
  }
);
